export interface PlaylistDurationRefreshMarker {
  playlistId: string;
  baselineDurationSeconds: number;
  createdAt: number;
}

const MARKER_PREFIX = 'playlist-duration-refresh:';

export const PLAYLIST_DURATION_REFRESH_TTL_MS = 2 * 60 * 1000;
export const PLAYLIST_DURATION_REFRESH_INTERVAL_MS = 2000;
export const PLAYLIST_DURATION_REFRESH_MAX_RETRIES = Math.ceil(
  PLAYLIST_DURATION_REFRESH_TTL_MS / PLAYLIST_DURATION_REFRESH_INTERVAL_MS
);

const getMarkerKey = (playlistId: string): string => `${MARKER_PREFIX}${playlistId}`;

export const writePlaylistDurationRefreshMarker = ({
  playlistId,
  baselineDurationSeconds,
  createdAt = Date.now(),
}: {
  playlistId: string;
  baselineDurationSeconds: number;
  createdAt?: number;
}): void => {
  if (!playlistId || typeof window === 'undefined' || !window.sessionStorage) return;

  window.sessionStorage.setItem(
    getMarkerKey(playlistId),
    JSON.stringify({
      playlistId,
      baselineDurationSeconds,
      createdAt,
    })
  );
};

export const readPlaylistDurationRefreshMarker = (
  playlistId: string
): PlaylistDurationRefreshMarker | null => {
  if (!playlistId || typeof window === 'undefined' || !window.sessionStorage) return null;

  const raw = window.sessionStorage.getItem(getMarkerKey(playlistId));
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw) as PlaylistDurationRefreshMarker;
    if (
      !parsed ||
      parsed.playlistId !== playlistId ||
      typeof parsed.baselineDurationSeconds !== 'number' ||
      typeof parsed.createdAt !== 'number'
    ) {
      return null;
    }

    return parsed;
  } catch {
    return null;
  }
};

export const clearPlaylistDurationRefreshMarker = (playlistId: string): void => {
  if (!playlistId || typeof window === 'undefined' || !window.sessionStorage) return;
  window.sessionStorage.removeItem(getMarkerKey(playlistId));
};

export const isPlaylistDurationRefreshMarkerExpired = (
  marker: PlaylistDurationRefreshMarker,
  now = Date.now(),
  ttlMs = PLAYLIST_DURATION_REFRESH_TTL_MS
): boolean => now - marker.createdAt >= ttlMs;
